import { useCallback, useState } from 'react';
import { useGoogleOAuth } from '@react-oauth/google';

/** The OAuth client the app was built with. Empty when the build had no
 *  REACT_APP_GOOGLE_CLIENT_ID, in which case no Google button can work. */
export const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

/**
 * Whether a Google sign-in button can actually render right now.
 *
 * `<GoogleLogin>` renders nothing at all when `accounts.google.com/gsi/client`
 * fails to load — blocked by CSP, blocked by an extension, offline, or served a
 * 503. Pages paired it with an "or" divider that rendered unconditionally, so a
 * third-party failure left a divider separating the sign-in form from empty
 * space, implying a missing option rather than a broken one.
 *
 * Gate both the divider and the button on this so the form degrades to
 * email-and-password cleanly.
 */
export function useGoogleSignInAvailable(): boolean {
  const { scriptLoadedSuccessfully } = useGoogleOAuth();
  return !!GOOGLE_CLIENT_ID && scriptLoadedSuccessfully;
}

/* Google Identity Services renders the button into an iframe it sizes itself,
 * and it only accepts a pixel COUNT for `width` — between these bounds. */
const GSI_MIN_WIDTH = 200;
const GSI_MAX_WIDTH = 400;

/**
 * A `width` for `<GoogleLogin>` that GSI will actually honour.
 *
 * Pages passed `width="100%"`, which GSI rejects — every render logged
 * `[GSI_LOGGER]: Provided button width is invalid: 100%` to the console and the
 * button fell back to its default size. The full-bleed look came from CSS
 * stretching the wrapper, not from the prop, so the prop was pure noise.
 *
 * Measure the wrapper instead and hand GSI a number it accepts, clamped to the
 * range it supports. Returns `undefined` until the wrapper has been measured, so
 * callers can hold the button back rather than mount it on a guess: `width` is
 * in `<GoogleLogin>`'s effect deps AND that effect calls
 * `google.accounts.id.initialize()`, so correcting the width afterwards just
 * trades the old warning for "initialize() is called multiple times".
 *
 * A CALLBACK REF, not an object ref with a layout effect. The wrapper is itself
 * behind `useGoogleSignInAvailable()`, which is false until Google's script
 * loads — so on first commit there is no node to measure, and a `[]`-deps effect
 * would never run again once one appeared. The width would stay `undefined` and
 * the sign-in button would never render at all.
 *
 * @returns `[ref to put on the wrapper, width to pass to <GoogleLogin>]`
 */
export function useGoogleButtonWidth(): [(el: HTMLDivElement | null) => void, string | undefined] {
  const [width, setWidth] = useState<string | undefined>(undefined);

  const attach = useCallback((el: HTMLDivElement | null) => {
    if (!el) return;
    const measure = () => {
      const w = el.getBoundingClientRect().width;
      if (!w) return; // hidden or not laid out yet — keep the last good value
      const clamped = Math.round(Math.min(GSI_MAX_WIDTH, Math.max(GSI_MIN_WIDTH, w)));
      // Only re-render when the value really changes. Both bounds cover whole
      // classes of viewport (desktop clamps to the max, narrow phones to the
      // min), so most resizes produce no change and no re-initialise.
      setWidth((prev) => (prev === String(clamped) ? prev : String(clamped)));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [attach, width];
}
